import React, { useState } from 'react';
import {
    ImageList,
    ImageListItem,
    Box,
    SxProps,
    Theme,
    useMediaQuery,
    useTheme,
    IconButton,
    Tooltip,
} from '@mui/material';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import 'react-photo-view/dist/react-photo-view.css';
import { Close } from '@mui/icons-material';

interface PhotoGridProps {
    photos?: string[];
    columns?: number;
    sx?: SxProps<Theme>;
    isEditMode?: boolean;
    onAddPhoto?: () => void;
    onEditClick?: (index: number) => void;
}

export const PhotoGrid: React.FC<PhotoGridProps> = ({
    photos = [],
    columns = 3,
    sx,
    isEditMode,
    onAddPhoto,
    onEditClick,
}) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const [brokenImages, setBrokenImages] = useState<Record<number, boolean>>({});

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));

    const column = isXs ? 2 : columns;
    const imageHeight = 100; // Fixed grid row height

    return (
        <PhotoProvider>
            <ImageList
                sx={{
                    width: '100%',
                    mt: 1,
                    overflow: 'visible',
                    ...sx,
                }}
                cols={column}
                rowHeight={imageHeight}>
                {photos.map((url, index) => {
                    const base = `${url}`;
                    const src = isEditMode ? url : base;
                    const srcSet = isEditMode ? undefined : `${base} , ${base}`;

                    return (
                        <ImageListItem
                            key={index}
                            onMouseEnter={() => setHoverIndex(index)}
                            onMouseLeave={() => setHoverIndex(null)}
                            sx={{
                                width: '100%',
                                height: imageHeight,
                                position: 'relative',
                                cursor: 'pointer',
                            }}>
                            <PhotoView
                                src={brokenImages[index] ? '/apple-touch-icon.png' : url}
                                key={index}
                                triggers={['onClick', 'onDoubleClick']}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}>
                                    <img
                                        src={brokenImages[index] ? '/apple-touch-icon.png' : src}
                                        srcSet={brokenImages[index] ? undefined : srcSet}
                                        sizes="(max-width: 600px) 50vw, 33vw"
                                        alt={`profile-${index}`}
                                        loading="lazy"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: 4,
                                            display: 'block',
                                            cursor: 'pointer',
                                        }}
                                        onError={e => {
                                            setBrokenImages(prev => ({ ...prev, [index]: true }));
                                        }}
                                    />

                                    {/* Hover zoom hint */}
                                    {hoverIndex === index && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                backgroundColor: 'rgba(0,0,0,0.35)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 1,
                                                transition: 'opacity 0.2s ease-in-out',
                                                pointerEvents: 'none',
                                            }}>
                                            <ZoomInIcon sx={{ color: 'white', fontSize: 30 }} />
                                        </Box>
                                    )}

                                    {/* Edit button in edit mode */}
                                    {isEditMode && onEditClick && (
                                        <Tooltip title="Edit photo">
                                            <IconButton
                                                size="small"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onEditClick(index);
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    right: 6,
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: 'white',
                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                                                }}>
                                                <Close />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </PhotoView>
                        </ImageListItem>
                    );
                })}

                {/* Add Photo Button in Edit Mode */}
                {isEditMode && (
                    <ImageListItem
                        sx={{
                            width: '100%',
                            height: imageHeight,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <IconButton
                            onClick={onAddPhoto}
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                            }}>
                            <AddPhotoAlternateIcon sx={{ fontSize: 40, color: 'gray' }} />
                        </IconButton>
                    </ImageListItem>
                )}
            </ImageList>
        </PhotoProvider>
    );
};
